// ProjectOverviewChart.jsx
import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";

const chartHeaderData = [
  { title: "Week", type: "week" },
  { title: "Month", type: "month" },
  { title: "Year", type: "year" },
  { title: "All", type: "all" },
];

// ✅ Safe CSS variable reader (never returns empty / var(--...))
const cssVar = (name, fallback) => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name);
    const s = String(v || "").trim();
    if (!s || s.includes("var(")) return fallback;
    return s;
  } catch {
    return fallback;
  }
};

const toNumArr = (arr) =>
  (Array.isArray(arr) ? arr : []).map((v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  });

const toStrArr = (arr) =>
  (Array.isArray(arr) ? arr : []).map((v) => (v == null ? "" : String(v)));

const ProjectOverviewChart = ({
  height = 350,
  dashboardChart,
  viewType = "water", // "water" | "slrm"
}) => {
  const [activeType, setActiveType] = useState("week");

  const cur = dashboardChart?.[activeType] || { labels: [], water: [], slrm: [] };

  const labels = useMemo(() => toStrArr(cur.labels), [cur.labels]);
  const waterArr = useMemo(() => toNumArr(cur.water), [cur.water]);
  const slrmArr = useMemo(() => toNumArr(cur.slrm), [cur.slrm]);

  const selectedArr = useMemo(
    () => (viewType === "slrm" ? slrmArr : waterArr),
    [viewType, slrmArr, waterArr]
  );

  const hasData =
    labels.length > 0 && selectedArr.length > 0 && selectedArr.length === labels.length;

  const totalSelected = useMemo(
    () => selectedArr.reduce((s, v) => s + (Number(v) || 0), 0),
    [selectedArr]
  );

  const series = useMemo(() => {
    return [
      { name: "Collection", type: "column", data: selectedArr },
      {
        name: viewType === "slrm" ? "SLRM" : "Water",
        type: "area",
        data: selectedArr,
      },
    ];
  }, [selectedArr, viewType]);

  const primary = cssVar("--primary", "#0d6efd");
  const green = "#3AC977";
  const red = "#FF5E5E";

  const options = useMemo(() => {
    return {
      chart: {
        height,
        type: "line",
        stacked: false,
        toolbar: { show: false },

        // ✅ IMPORTANT: prevent PathArray crash during transitions
        animations: { enabled: false },
      },

      stroke: { width: [0, 2], curve: "straight" },

      legend: {
        fontSize: "13px",
        fontFamily: "poppins",
        labels: { colors: "#888888" },
      },

      plotOptions: {
        bar: { columnWidth: "20%", borderRadius: 6 },
      },

      colors: viewType === "slrm" ? [primary, red] : [primary, green],
      markers: { size: 0 },

      xaxis: {
        categories: labels,
        type: "category",
        labels: { style: { fontSize: "13px", colors: "#888888" } },
      },

      yaxis: {
        min: 0,
        tickAmount: 4,
        labels: { style: { fontSize: "13px", colors: "#888888" } },
      },

      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (y) =>
            typeof y !== "undefined" ? `₹${Number(y || 0).toFixed(0)}` : "",
        },
      },

      noData: { text: "No data" },
    };
  }, [labels, height, viewType, primary]);

  return (
    <div className="card overflow-hidden">
      <div className="card-header border-0 pb-0 flex-wrap d-flex justify-content-between align-items-center">
        <h4 className="heading mb-0">
          {viewType === "slrm"
            ? "SLRM Collections Overview"
            : "Water Collections Overview"}
        </h4>

        {/* ✅ Use simple buttons instead of Tab.Container */}
        <div className="btn-group mix-chart-tab">
          {chartHeaderData.map((item) => (
            <button
              key={item.type}
              type="button"
              className={`btn btn-sm me-2 ${
                activeType === item.type ? "btn-primary" : "btn-light"
              }`}
              onClick={() => setActiveType(item.type)}
            >
              {item.title}
            </button>
          ))}
        </div>
      </div>

      <div className="card-body p-0">
        <div id="overiewChart">
          {hasData ? (
            <ReactApexChart
              // ✅ FORCE FULL REMOUNT on viewType/tab switch
              key={`${viewType}-${activeType}-${labels.length}`}
              options={options}
              series={series}
              type="line"
              height={height}
            />
          ) : (
            <div className="text-center py-5 text-muted">No data available</div>
          )}
        </div>

        <div className="ttl-project">
          <div className="pr-data">
            <h5>₹{totalSelected.toFixed(0)}</h5>
            <span>{viewType === "slrm" ? "SLRM Collection" : "Water Collection"}</span>
          </div>

          <div className="pr-data">
            <h5 className={viewType === "slrm" ? "text-danger" : "text-success"}>
              ₹{totalSelected.toFixed(0)}
            </h5>
            <span>{viewType === "slrm" ? "SLRM" : "Water"}</span>
          </div>

          <div className="pr-data">
            <h5 className="text-primary">{labels.length}</h5>
            <span>Points</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOverviewChart;
